// M09 多項式、餘式及因式定理 - 主數據文件
// Polynomials, Remainder and Factor Theorem - Main Data File
// 系統化設計，準備後續 Supabase 數據遷移

// 導入所有主題和問題數據
import {
  M09_TOPICS_01_TO_10,
  M09_PROBLEMS_01_TO_10
} from './M09_Topics_01_to_10.js';

import {
  M09_TOPICS_11_TO_20,
  M09_PROBLEMS_11_TO_20
} from './M09_Topics_11_to_20.js';

import {
  M09_TOPICS_21_TO_26,
  M09_PROBLEMS_21_TO_26
} from './M09_Topics_21_to_26.js';

// M09 模組基本信息
export const M09_MODULE_INFO = {
  id: 'M09',
  name: '多項式、餘式及因式定理',
  description: '全面掌握多項式運算、餘式定理應用、因式分解技巧與方程求解',
  category: 'advanced',
  difficulty: 'advanced',
  estimatedTime: '20-28小時',
  totalQuestions: 130,
  totalTopics: 26,
  questionsPerTopic: 5,
  partsPerQuestion: 3,
  difficultyDistribution: {
    basic: 25,
    intermediate: 70,
    advanced: 35
  },
  prerequisites: ['M01', 'M02', 'M07', 'M08'],
  learningObjectives: [
    '掌握多項式基本運算與性質',
    '理解並應用餘式定理',
    '熟練運用因式定理進行分解',
    '掌握高次方程的求解方法',
    '理解多項式函數的圖像性質',
    '應用多項式解決實際問題'
  ],
  keyTopics: [
    '聯立方程', '導數應用', '餘式定理證明', '餘式定理應用', 
    '餘式定理進階', '複數餘式定理', '遞迴求解', '綜合除法',
    '對稱多項式', '除法算法', '特殊解分析', '重根條件',
    '待定係數', '係數比較', '直接代入', '有理根定理',
    '多項式構造', '多項式插值', '多項式除法算法', '多項式恆等',
    '因式定理', '因式分解進階', '因式分解', '代數變換',
    '代數運算', '方程求解'
  ],
  assessmentCriteria: {
    conceptualUnderstanding: 0.30,
    procedualFluency: 0.35,
    strategicCompetence: 0.25,
    adaptiveReasoning: 0.10
  },
  metadata: {
    createdDate: '2024-01-15',
    lastUpdated: '2024-01-15',
    version: '1.0.0',
    author: 'RightMaths Education Team',
    reviewStatus: 'approved',
    tags: ['polynomials', 'remainder_theorem', 'factor_theorem', 'advanced_algebra', 'hkdse']
  }
};

// 聚合所有主題數據
export const M09_TOPICS = [
  ...M09_TOPICS_01_TO_10,
  ...M09_TOPICS_11_TO_20,
  ...M09_TOPICS_21_TO_26
];

// 聚合所有問題數據
export const M09_PROBLEMS_DATA = [
  ...M09_PROBLEMS_01_TO_10,
  ...M09_PROBLEMS_11_TO_20,
  ...M09_PROBLEMS_21_TO_26
];

// 數據訪問輔助函數
export function getAllM09Problems() {
  return M09_PROBLEMS_DATA;
}

export function getM09ProblemsByTopic(topicId) {
  return M09_PROBLEMS_DATA.filter(problem => problem.topicId === topicId);
}

export function getM09ProblemsByDifficulty(difficulty) {
  return M09_PROBLEMS_DATA.filter(problem => problem.difficulty === difficulty);
}

export function getM09TopicById(topicId) {
  return M09_TOPICS.find(topic => topic.id === topicId);
}

export function getM09ProblemById(problemId) {
  return M09_PROBLEMS_DATA.find(problem => problem.id === problemId);
}

// 數據統計函數
export function getM09Statistics() {
  const difficultyCount = M09_PROBLEMS_DATA.reduce((acc, problem) => {
    acc[problem.difficulty] = (acc[problem.difficulty] || 0) + 1;
    return acc;
  }, {});

  const categoryCount = M09_TOPICS.reduce((acc, topic) => {
    acc[topic.category] = (acc[topic.category] || 0) + 1;
    return acc;
  }, {});

  return {
    totalTopics: M09_TOPICS.length,
    totalProblems: M09_PROBLEMS_DATA.length,
    totalParts: M09_PROBLEMS_DATA.length * 3, // 每題3部分
    difficultyDistribution: difficultyCount,
    categoryDistribution: categoryCount,
    averageProblemsPerTopic: Math.round(M09_PROBLEMS_DATA.length / M09_TOPICS.length * 10) / 10
  };
}

// 數據驗證函數
export function validateM09Data() {
  const issues = [];
  
  // 檢查主題數量
  if (M09_TOPICS.length !== 26) {
    issues.push(`Expected 26 topics, found ${M09_TOPICS.length}`);
  }
  
  // 檢查問題數量
  if (M09_PROBLEMS_DATA.length !== 130) {
    issues.push(`Expected 130 problems, found ${M09_PROBLEMS_DATA.length}`);
  }
  
  // 檢查每個主題的問題數量
  const topicProblemCounts = {};
  M09_PROBLEMS_DATA.forEach(problem => {
    topicProblemCounts[problem.topicId] = (topicProblemCounts[problem.topicId] || 0) + 1;
  });
  
  M09_TOPICS.forEach(topic => {
    const count = topicProblemCounts[topic.id] || 0;
    if (count !== 5) {
      issues.push(`Topic ${topic.id} has ${count} problems, expected 5`);
    }
  });
  
  // 檢查問題結構
  M09_PROBLEMS_DATA.forEach(problem => {
    if (!problem.parts || !problem.parts.a || !problem.parts.b || !problem.parts.c) {
      issues.push(`Problem ${problem.id} missing required parts`);
    }
    
    ['a', 'b', 'c'].forEach(part => {
      if (problem.parts[part]) {
        if (!problem.parts[part].solution || !problem.parts[part].solution.steps) {
          issues.push(`Problem ${problem.id} part ${part} missing solution steps`);
        }
        if (!problem.parts[part].hint) {
          issues.push(`Problem ${problem.id} part ${part} missing hint`);
        }
      }
    });
  });
  
  return {
    isValid: issues.length === 0,
    issues: issues,
    summary: `Validation ${issues.length === 0 ? 'passed' : 'failed'} with ${issues.length} issues`
  };
}

// Supabase 遷移準備函數
export function prepareM09ForSupabase() {
  const moduleData = {
    module_info: {
      ...M09_MODULE_INFO,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    },
    topics: M09_TOPICS.map(topic => ({
      ...topic,
      module_id: 'M09',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    })),
    problems: M09_PROBLEMS_DATA.map(problem => ({
      ...problem,
      module_id: 'M09',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      // 將 parts 結構平坦化以適應關係型數據庫
      part_a_question: problem.parts.a.question,
      part_a_solution: JSON.stringify(problem.parts.a.solution),
      part_a_hint: problem.parts.a.hint,
      part_b_question: problem.parts.b.question,
      part_b_solution: JSON.stringify(problem.parts.b.solution),
      part_b_hint: problem.parts.b.hint,
      part_c_question: problem.parts.c.question,
      part_c_solution: JSON.stringify(problem.parts.c.solution),
      part_c_hint: problem.parts.c.hint
    }))
  };
  
  return {
    data: moduleData,
    tableStructures: {
      modules: ['id', 'name', 'description', 'category', 'difficulty', 'total_questions', 'total_topics', 'created_at', 'updated_at'],
      topics: ['id', 'module_id', 'name', 'description', 'category', 'difficulty', 'created_at', 'updated_at'],
      problems: ['id', 'module_id', 'topic_id', 'type', 'difficulty', 'question', 'part_a_question', 'part_a_solution', 'part_a_hint', 'part_b_question', 'part_b_solution', 'part_b_hint', 'part_c_question', 'part_c_solution', 'part_c_hint', 'created_at', 'updated_at']
    },
    migrationScript: `
      -- M09 Module Migration Script
      INSERT INTO modules (id, name, description, category, difficulty, total_questions, total_topics, created_at, updated_at)
      VALUES ('M09', '多項式、餘式及因式定理', '全面掌握多項式運算、餘式定理應用、因式分解技巧與方程求解', 'advanced', 'advanced', 130, 26, NOW(), NOW());
      
      -- Insert topics (26 topics)
      -- Insert problems (130 problems)
      -- See generated data for complete INSERT statements
    `
  };
}

// 導出默認對象
export default {
  moduleInfo: M09_MODULE_INFO,
  topics: M09_TOPICS,
  problems: M09_PROBLEMS_DATA,
  getAllProblems: getAllM09Problems,
  getProblemsByTopic: getM09ProblemsByTopic,
  getProblemsByDifficulty: getM09ProblemsByDifficulty,
  getTopicById: getM09TopicById,
  getProblemById: getM09ProblemById,
  getStatistics: getM09Statistics,
  validateData: validateM09Data,
  prepareForSupabase: prepareM09ForSupabase
}; 